import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button, NavBar, NavBarItem, useModal } from '../../../../../../../components'
import {
  AnotacaoType,
  ButtonType,
  ControlError,
  IconPaths,
  KTCard,
  KTSVG,
  ModalSize,
  QueryState,
  showAlert,
  showAlertLoading,
  showError,
  stringifyRequestQuery,
  getApiErrorMessage,
} from '../../../../../../../../_metronic/helpers'
import { adicionarAnotacao, getAtendimentoById } from '../../../../../atendimento/core/_requests'
import { useSearchParams } from 'react-router-dom'
import {
  ExameDtoPrescricao,
  MedicamentoDtoPrescricao,
  PrescricaoDtoExame,
  PrescricaoDtoMedicamento,
  PrescricaoDtoOrientacao,
} from '../../../../prescricao/core/_model'
import {
  createExame,
  createMedicamento,
  createOrientacao,
  deletePrescricao,
  getExames,
  getMedicamentos,
} from '../../../../prescricao/core/_requests'
import { AtendimentoDtoAnotacao, AtendimentoView } from '../../../../../atendimento/core/_models'
import { Medicamento, ReceitaIAModal } from './ReceitaIAModal'
import { useFormik } from 'formik'
import { HipoteseIAModal } from './HipoteseIAModal'
import { resultadoIASchema } from './validation/resultadoIASchema'
import { OrientacoesIA, OrientacoesIAModal } from './OrientacoesIAModal'
import {
  DocumentoModal,
  DocumentoModalRef,
} from '../../../../prescricao/components/modal/DocumentoModal'
import { ExameComplementar, ExameIAModal } from './ExameIAModal'
import { usePrescricaoUtils } from '../../../../prescricao/utils/usePrescricaoUtils'

export type ResultJSON = {
  diagnostico: string
  receita: Medicamento[]
  orientacoes_medicas: OrientacoesIA
  exames_complementares: ExameComplementar[]
}

type NavbarItem = {
  label: NavBarLabel
  value: string | OrientacoesIA | Medicamento[] | ExameComplementar[] | undefined
}

type IResultadoIAModal = {
  resultData: ResultJSON
  prompt: string
  onChangeResultData: (newData: ResultJSON) => void
}

export type NavBarLabel = 'Hipótese' | 'Orientações Médicas' | 'Medicamentos' | 'Exames'

export type ResultFormIA = ResultJSON & { navBar: NavBarLabel }

export type ResultadoIAModalRef = {
  onClose: () => void
}

export const ResultadoIAModal = forwardRef<ResultadoIAModalRef, IResultadoIAModal>(
  ({ resultData, prompt, onChangeResultData }, ref) => {
    useImperativeHandle(ref, () => ({
      onClose: resetPrescricao,
    }))

    const documentoModalRef = useRef<DocumentoModalRef>(null)
    const { showModal } = useModal()

    const [searchParams] = useSearchParams()
    const atendimentoId = String(searchParams.get('atendimento'))

    const [prescricoes, setPrescricoes] = useState<number[]>([])
    const [atendimento, setAtendimento] = useState<AtendimentoView>()

    const [navbarData] = useState<NavbarItem[]>([
      { label: 'Hipótese', value: resultData?.diagnostico },
      { label: 'Orientações Médicas', value: resultData?.orientacoes_medicas },
      { label: 'Medicamentos', value: resultData?.receita },
      { label: 'Exames', value: resultData?.exames_complementares },
    ])
    const { handleSignPrescriptions } = usePrescricaoUtils(atendimento)

    useEffect(() => {
      const getAtendimento = async () => {
        const atend = await getAtendimentoById(Number(atendimentoId))
        setAtendimento(atend)
      }
      getAtendimento()
    }, [atendimentoId])

    const formik = useFormik<ResultFormIA>({
      initialValues: { ...resultData, navBar: 'Hipótese' },
      validationSchema: resultadoIASchema,
      onSubmit: (values, { setSubmitting }) => {
        setSubmitting(true)
        try {
          resetPrescricao()
          switch (values.navBar) {
            case 'Hipótese':
              generateHipotese()
              break
            case 'Exames':
            case 'Orientações Médicas':
            case 'Medicamentos':
              generatePrescricao()
              break
          }
        } catch (error) {
          showError(error)
        } finally {
          setSubmitting(false)
        }
      },
    })

    const renderContent = () => {
      const selectedItem = navbarData.find((item) => item.label === formik.values.navBar)

      if (!selectedItem) return <p>Sem dados disponíveis.</p>

      switch (formik.values.navBar) {
        case 'Hipótese':
          return <HipoteseIAModal formik={formik} />
        case 'Orientações Médicas':
          return <OrientacoesIAModal onReset={resetPrescricao} formik={formik} />
        case 'Medicamentos':
          return <ReceitaIAModal onReset={resetPrescricao} formik={formik} />
        case 'Exames':
          return <ExameIAModal onReset={resetPrescricao} formik={formik} />
      }
    }

    const generatePrescricao = async () => {
      const prescriptionsIds = []
      try {
        showAlertLoading()
        if (formik.values.navBar === 'Medicamentos') {
          const medicamentosInsert: MedicamentoDtoPrescricao[] = []
          for (const medicamento of formik.values.receita) {
            if (medicamento.id) {
              medicamentosInsert.push({
                idMedicamento: medicamento.id,
                posologia: medicamento.posologia,
                quantidade: medicamento.quantidade,
                quantidadeLme: [],
              })
              continue
            }

            const state: QueryState = {
              page: 0,
              size: 10,
              // sem dosagem o critério sai fora: a IA manda null e "concentracao~*null*" dava 500 no backend
              search: [
                `nome:${medicamento.medicamento}`,
                medicamento.dosagem ? `concentracao~*${medicamento.dosagem}*` : '',
              ]
                .filter(Boolean)
                .join(','),
            }
            const medicamentos = await getMedicamentos(stringifyRequestQuery(state))

            if (medicamentos.data && medicamentos.data.length > 0) {
              const encontrado = medicamentos.data[0]
              medicamentosInsert.push({
                idMedicamento: Number(encontrado.id),
                posologia: medicamento.posologia,
                quantidade: medicamento.quantidade,
                quantidadeLme: [],
              })
            } else {
              medicamentosInsert.push({
                idMedicamento: undefined,
                medicamento: {
                  nome: medicamento.medicamento,
                  concentracao: medicamento.dosagem,
                  substancia: medicamento.medicamento,
                  receituario: medicamento.receituario?.value,
                },
                posologia: medicamento.posologia,
                quantidade: medicamento.quantidade,
                quantidadeLme: [],
              })
            }
          }

          const medicamentos: PrescricaoDtoMedicamento = {
            medicamentos: medicamentosInsert,
          }

          const prescricoes = await createMedicamento(Number(atendimentoId), medicamentos)

          prescriptionsIds.push(...prescricoes.map((prescricao) => prescricao.id))
        } else if (formik.values.navBar === 'Orientações Médicas') {
          const orientacoes: PrescricaoDtoOrientacao = {
            orientacoes: [
              {
                idOrientacao: undefined,
                orientacao: {
                  descricao: formik.values.orientacoes_medicas.orientacoes,
                  nome: formik.values.orientacoes_medicas.titulo,
                },
              },
            ],
          }

          const prescricoes = await createOrientacao(Number(atendimentoId), orientacoes)
          prescriptionsIds.push(...prescricoes.map((prescricao) => prescricao.id))
        } else if (formik.values.navBar === 'Exames') {
          const examesInsert: ExameDtoPrescricao[] = []
          for (const exame of formik.values.exames_complementares) {
            if (exame.id) {
              examesInsert.push({
                idExame: exame.id,
                instrucoes: exame.instrucoes,
              })
              continue
            }

            const state: QueryState = {
              page: 0,
              size: 10,
              search: `nome~*${exame.exame}*`,
            }
            const exames = await getExames(stringifyRequestQuery(state))

            if (exames.data && exames.data.length > 0) {
              const encontrado = exames.data[0]
              examesInsert.push({
                idExame: Number(encontrado.id),
                instrucoes: exame.instrucoes,
              })
            } else {
              examesInsert.push({
                idExame: undefined,
                exame: {
                  nome: exame.exame,
                },
                instrucoes: exame.instrucoes,
              })
            }
          }

          const exames: PrescricaoDtoExame = {
            exames: examesInsert,
          }

          const prescricoes = await createExame(Number(atendimentoId), exames)

          prescriptionsIds.push(...prescricoes.map((prescricao) => prescricao.id))
        }

        await handleSignPrescriptions(prescriptionsIds)
      } catch (error: any) {
        showError(error)
        if (getApiErrorMessage(error) === ControlError.InvalidPassword) {
          setPrescricoes(prescriptionsIds)
        }
      }
    }

    const generateHipotese = async () => {
      let anotacao = prompt
      anotacao += `<p><strong>${formik.values.navBar}:</strong> ${formik.values.diagnostico.replace(
        /\n/g,
        '<br>'
      )}</p>`
      try {
        const data: AtendimentoDtoAnotacao = {
          tipo: AnotacaoType.Anamnese,
          anotacao,
        }
        await adicionarAnotacao(Number(atendimento?.id), data)
        await showAlert({
          title: 'Sucesso!',
          text: 'Anotação criada com sucesso na Anamnese.',
          icon: 'success',
        })
      } catch (error) {
        showError(error)
      }
    }

    const resetPrescricao = async () => {
      for (const id of prescricoes) {
        await deletePrescricao(id)
      }
      setPrescricoes([])
    }

    useEffect(() => {
      onChangeResultData(formik.values)
    }, [formik.values, onChangeResultData])

    const handleDocumento = () => {
      resetPrescricao()
      showModal({
        title: 'Adicionar Documento',
        content: <DocumentoModal ref={documentoModalRef} />,
        fetchers: {
          atendimento: () => getAtendimentoById(Number(atendimentoId)),
        },
        size: ModalSize.Medium,
        onClose: () => documentoModalRef.current?.onClose(),
      })
    }

    const handleSign = async () => {
      try {
        const geradas = await handleSignPrescriptions(prescricoes)
        if (geradas) setPrescricoes([])
      } catch (error: any) {
        showError(error)
      }
    }

    return (
      <form onSubmit={formik.handleSubmit}>
        <KTCard className='bg-gray-400'>
          <NavBar>
            {navbarData.map(({ label }, index) => (
              <NavBarItem
                isActive={label === formik.values.navBar}
                onClick={() => {
                  resetPrescricao()
                  formik.setFieldValue('navBar', label)
                }}
                key={label}
                isFirst={index === 0}
                isLast={index === navbarData.length - 1}
              >
                {label}
              </NavBarItem>
            ))}
          </NavBar>
          <div className='content p-6'>{renderContent()}</div>
        </KTCard>
        <div className='d-flex align-items-center p-5 rounded bg-info-light text-dark mt-3'>
          <KTSVG path={IconPaths.Info} color='blue' className='svg-icon-1 me-2' />
          <span className='fs-6'>
            A funcionalidade de IA foi desenvolvida para atuar como um suporte complementar ao
            atendimento médico, não substituindo, em hipótese alguma, a análise clínica e a decisão
            profissional.
          </span>
        </div>
        <div className='d-flex flex-end mt-5'>
          <Button className='btn-secondary' onClick={handleDocumento}>
            Atestados e Encaminhamentos
          </Button>
          {formik.values.navBar === 'Hipótese' ? null : prescricoes.length > 0 ? (
            <Button className='btn-info active ms-2' onClick={handleSign}>
              Assinar Prescrição
            </Button>
          ) : (
            <Button type={ButtonType.Submit} className='btn-primary ms-2'>
              Gerar{' '}
              {formik.values.navBar === 'Medicamentos' || formik.values.navBar === 'Exames'
                ? 'Prescrição'
                : 'Orientações'}
            </Button>
          )}
        </div>
      </form>
    )
  }
)
